import { Network } from '@capacitor/network';
import { AlertsTypes } from '~/const/notification';
import {
	decodeQueuedBody,
	getOfflineRequests,
	removeOfflineRequest
} from '~/helpers/offlineRequestQueue';
import { getConnectionStatus, isNetworkAvailable } from '~/helpers/network';
import { useNotificationStore } from '~/store/notification.store';
import { useCustomFetch } from '~/composables/useCustomFetch';

const RETRY_DELAY = 15000;

export default defineNuxtPlugin((nuxtApp) => {
	let syncPromise: Promise<void> | null = null;
	let queueRetryTimer: ReturnType<typeof setTimeout> | null = null;
	const runtimeConfig = useRuntimeConfig();

	const getReplayUrl = (url: string) => {
		const baseURL = runtimeConfig.public.baseURL;
		if (baseURL && url.startsWith(baseURL)) {
			return url.slice(baseURL.length) || '/';
		}
		return url;
	};

	const synchronizeQueue = () => {
		if (syncPromise) return syncPromise;

		syncPromise = (async () => {
			if (!(await getConnectionStatus())) return;

			const requests = await getOfflineRequests();
			if (!requests.length) return;

			let sentCount = 0;
			let shouldRetry = false;
			for (const request of requests) {
				if (!(await getConnectionStatus())) {
					shouldRetry = false;
					break;
				}

				const result = await nuxtApp.runWithContext(() =>
					useCustomFetch(
						getReplayUrl(request.url),
						{
							method: request.method,
							query: request.query,
							body: decodeQueuedBody(request.body),
							headers: request.headers,
							server: false,
							watch: false,
							retry: false
						},
						{ skipOfflineQueue: true, isDisabledErrroMessage: true }
					)
				);

				if (result.status.value === 'success') {
					await removeOfflineRequest(request.id);
					const replayUrl = getReplayUrl(request.url).split('?')[0];
					if (request.method === 'POST' && replayUrl.endsWith('/api/CargoImage/upload')) {
						const cargoId = request.body?.type === 'form-data'
							? request.body.entries.find((entry) => entry.name === 'cargoId')?.value
							: undefined;
						const query = request.query as Record<string, unknown> | undefined;
						if (typeof cargoId === 'string') {
							window.dispatchEvent(
								new CustomEvent('cargo-image-uploaded', {
									detail: {
										cargoId,
										cargoImageType: String(query?.cargoImageType ?? ''),
										offlineRequestId: request.id
									}
								})
							);
						}
					}
					sentCount += 1;
					continue;
				}

				const error = result.error.value as { statusCode?: number; response?: { status?: number } } | null;
				const statusCode = error?.statusCode || error?.response?.status;
				if (statusCode && statusCode >= 400 && statusCode < 500) {
					await removeOfflineRequest(request.id);
					if (statusCode === 401) break;
					useNotificationStore().addNewAlert(
						'Сервер отклонил сохранённый офлайн-запрос. Проверьте данные и повторите действие.',
						5000,
						AlertsTypes.FAILURE
					);
					continue;
				}

				shouldRetry = true;
				break;
			}

			if (sentCount) {
				const suffix = sentCount === 1 ? 'запрос отправлен' : 'запросов отправлено';
				useNotificationStore().addNewAlert(
					`После восстановления сети ${sentCount} ${suffix}.`,
					4000,
					AlertsTypes.SUCCESS
				);
			}

			if (shouldRetry && (await getConnectionStatus())) {
				queueRetryTimer = setTimeout(() => {
					void synchronizeQueue().catch((error) => {
						console.error('Failed to synchronize offline requests:', error);
					});
				}, RETRY_DELAY);
			}
		})().finally(() => {
			syncPromise = null;
		});

		return syncPromise;
	};

	const startSynchronization = () => {
		if (queueRetryTimer) {
			clearTimeout(queueRetryTimer);
			queueRetryTimer = null;
		}
		void synchronizeQueue().catch((error) => {
			console.error('Failed to synchronize offline requests:', error);
		});
	};

	window.addEventListener('online', startSynchronization);
	window.addEventListener('offline-queue-updated', startSynchronization);
	void Network.addListener('networkStatusChange', (status) => {
		if (isNetworkAvailable(status)) startSynchronization();
	}).catch((error) => {
		console.error('Failed to listen for network status changes:', error);
	});

	nuxtApp.hook('app:mounted', startSynchronization);
});
